import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", channel: "website", send_discord: false, publish_at: "" });

  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/announcements", { ...form, publish_at: form.publish_at || null });
      toast.success("Announcement published");
      setForm({ title: "", body: "", channel: "website", send_discord: false, publish_at: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-announcements">
      <PageTitle title="Announcements" sub="Broadcast news to the website and Discord." />
      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={create} className="lg:col-span-2 rounded-lg border border-white/5 bg-panel p-6 space-y-4 h-fit" data-testid="announcement-form">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">New announcement</p>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Title" data-testid="announcement-title"
            className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={4} placeholder="What's happening?" data-testid="announcement-body"
            className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple resize-none" />
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="announcement-channel"
            className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none [&>option]:bg-panel">
            <option value="website">Website banner</option>
            <option value="maintenance">Maintenance notice</option>
            <option value="news">News post</option>
          </select>
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={form.send_discord} onChange={(e) => setForm({ ...form, send_discord: e.target.checked })} data-testid="announcement-discord" className="accent-[#5865F2] w-4 h-4" />
            Also send to Discord support server
          </label>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/50">Schedule (optional)</label>
            <input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} data-testid="announcement-schedule"
              className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple [color-scheme:dark]" />
          </div>
          <button type="submit" data-testid="announcement-publish"
            className="w-full inline-flex items-center justify-center gap-2 bg-blurple hover:bg-[#4752c4] text-white font-semibold py-3 rounded-full transition-colors duration-150">
            <Plus size={15} /> Publish
          </button>
        </form>

        <div className="lg:col-span-3 space-y-3" data-testid="announcements-list">
          {items.map((a) => (
            <div key={a.id} className="rounded-lg border border-white/5 bg-panel p-5 flex items-start justify-between gap-4" data-testid={`announcement-${a.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-bold text-white">{a.title}</p>
                  <Badge tone="blue">{a.channel}</Badge>
                  {a.send_discord && <Badge tone="cyan">discord</Badge>}
                </div>
                <p className="text-sm text-white/60 mt-1.5 leading-relaxed">{a.body}</p>
                <p className="text-[11px] font-mono text-white/30 mt-2">{new Date(a.publish_at).toLocaleString()}</p>
              </div>
              <button onClick={() => remove(a.id)} className="text-white/30 hover:text-red-400 transition-colors duration-150 shrink-0" data-testid={`announcement-delete-${a.id}`} aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          ))}
          {!items.length && <p className="text-white/40 text-sm font-mono">No announcements yet.</p>}
        </div>
      </div>
    </div>
  );
}
